<script setup lang="ts">
import { computed } from 'vue';

type NoticeKind = 'info' | 'ok' | 'warn' | 'err';

const props = withDefaults(defineProps<{ kind?: NoticeKind; title?: string }>(), {
  kind: 'info',
});

const ICON: Record<NoticeKind, string> = { info: 'i', ok: '✓', warn: '!', err: '!' };
const icon = computed(() => ICON[props.kind]);
</script>

<template>
  <div
    class="dt-notice"
    :class="`dt-notice--${kind}`"
    role="status"
  >
    <span
      class="dt-notice__icon"
      aria-hidden="true"
    >{{ icon }}</span>
    <div class="dt-notice__body">
      <p
        v-if="title"
        class="dt-notice__title"
      >
        {{ title }}
      </p>
      <div class="dt-notice__content">
        <slot />
      </div>
      <div
        v-if="$slots.action"
        class="dt-notice__action"
      >
        <slot name="action" />
      </div>
    </div>
  </div>
</template>

<style scoped>
.dt-notice {
  display: flex;
  gap: var(--dt-notice-gap);
  align-items: flex-start;
  padding: var(--dt-notice-padding);
  border: var(--dt-notice-border-width) solid transparent;
  border-radius: var(--dt-notice-radius);
  font-size: var(--dt-notice-font-size);
}

/* Glifo colorido SEM círculo de fundo (ui.jsx:29, styles.css:121) — a cor vem da variante. */
.dt-notice__icon {
  flex: 0 0 auto;
  line-height: var(--dt-line-height-flat);
  font-weight: var(--dt-notice-icon-font-weight);
  font-size: var(--dt-notice-icon-font-size);
}

.dt-notice__title {
  margin: 0 0 var(--dt-notice-title-margin-bottom);
  font-size: var(--dt-notice-title-size);
  font-weight: var(--dt-notice-title-weight);
  color: var(--dt-notice-title-color);
}
/* Título na cor da variante (styles.css:122-127 — .nt herda a cor da notice). */
.dt-notice--info .dt-notice__title { color: var(--dt-info); }
.dt-notice--ok .dt-notice__title { color: var(--dt-success); }
.dt-notice--warn .dt-notice__title { color: var(--dt-warning); }
.dt-notice--err .dt-notice__title { color: var(--dt-error); }

.dt-notice__content {
  color: var(--dt-text);
  opacity: 0.86; /* .nd do protótipo (styles.css:123) */
}

.dt-notice__action {
  margin-top: var(--dt-notice-action-margin-top);
}

.dt-notice--info {
  background: var(--dt-info-bg);
  border-color: var(--dt-notice-border-color-info);
}
.dt-notice--info .dt-notice__icon {
  color: var(--dt-info);
}

.dt-notice--ok {
  background: var(--dt-success-bg);
  border-color: var(--dt-notice-border-color-ok);
}
.dt-notice--ok .dt-notice__icon {
  color: var(--dt-success);
}

.dt-notice--warn {
  background: var(--dt-warning-bg);
  border-color: var(--dt-notice-border-color-warn);
}
.dt-notice--warn .dt-notice__icon {
  color: var(--dt-warning);
}

.dt-notice--err {
  background: var(--dt-error-bg);
  border-color: var(--dt-notice-border-color-err);
}
.dt-notice--err .dt-notice__icon {
  color: var(--dt-error);
}
</style>
